import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";

const WeeklyTournament = lazy(() =>
  import("@/components/games/WeeklyTournament").then((m) => ({ default: m.WeeklyTournament })),
);

const TITLE = "Palworld Weekly Arcade Tournament — Free Entry";
const DESCRIPTION =
  "Enter the free weekly Palworld arcade tournament: three featured Pal Stars games, points for your best run in each and a shareable score card every week.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Check this week's lineup", text: "Three arcade games are featured each tournament week and rotate every Monday at 00:00 UTC." },
  { name: "Post a run in each game", text: "Play each featured game once or more; your best run of the week is the one that scores." },
  { name: "Score points", text: "Each featured game contributes up to 100 points, so all three matter more than a single big run." },
  { name: "Share your card", text: "Copy the weekly score card and post it — the link brings friends straight into the same week." },
];

const FAQ: FaqItem[] = [
  {
    q: "What is the Palworld weekly arcade tournament?",
    a: "It is a free weekly competition across the Pal Stars Palworld arcade. Three games are featured each week, your best run in each one scores up to 100 points, and the total is a shareable score card. The week runs Monday 00:00 UTC to the following Monday 00:00 UTC.",
  },
  {
    q: "Do I need an account to enter the weekly tournament?",
    a: "No. Tournament entries are tracked in your browser, so you can play and share without signing up. A free account is only needed for the account leaderboard, daily booster packs and Palsino spins.",
  },
  {
    q: "Does the weekly Palworld tournament pay rewards?",
    a: "No packs, spins or gold — it is cosmetic. You compete for points, achievement badges and a shareable score card, which keeps the arcade impossible to farm.",
  },
  {
    q: "How are the three featured games chosen?",
    a: "The lineup is derived from the week id, so it is the same for everyone and rotates automatically every Monday without anyone picking favourites.",
  },
  {
    q: "How are tournament points calculated?",
    a: "Each featured game contributes its best score of the week, capped at 100 points, and the three are added together. Entering all three games always beats maxing out one.",
  },
];

export const Route = createFileRoute("/weekly-tournament")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld weekly arcade tournament — free to enter" },
      { property: "og:description", content: "Three featured Palworld games per week, points for your best run in each and a shareable weekly score card." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/weekly-tournament" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/weekly-tournament" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Weekly Tournament", "/weekly-tournament"]]),
      toolSchema({ name: "Palworld weekly arcade tournament", description: DESCRIPTION, path: "/weekly-tournament" }),
      howToSchema({
        name: "How to enter the Palworld weekly arcade tournament",
        description: "Play the week's three featured Palworld games, score points with your best run in each and share the card.",
        path: "/weekly-tournament",
        steps: STEPS,
      }),
      faqSchema(FAQ, "https://palstars.com/weekly-tournament"),
    ],
  }),
  component: WeeklyTournamentPage,
});

function WeeklyTournamentPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · weekly competition</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld weekly arcade tournament</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          The Palworld weekly arcade tournament is free to enter and needs no account: three Pal Stars games are featured
          each week, your best run in each scores up to 100 points, and the week closes every Monday at 00:00 UTC with a
          shareable score card.
        </p>
      </header>

      <Suspense
        fallback={
          <section className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold">Weekly Palworld arcade tournament</h2>
            <p className="mt-2 text-sm text-muted-foreground">Loading this week's lineup…</p>
          </section>
        }
      >
        <WeeklyTournament />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How the tournament works</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Keep playing</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/daily-puzzle" className="font-semibold text-primary hover:underline">Palworld Pal Puzzle →</Link> daily game with spin rewards.</li>
          <li><Link to="/pal-catch" className="font-semibold text-primary hover:underline">Pal Sphere Toss →</Link> normal and hard modes.</li>
          <li><Link to="/card-match" className="font-semibold text-primary hover:underline">TCG memory match →</Link> 8 or 12 pair boards.</li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld weekly tournament FAQ" />
    </div>
  );
}
